import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { addDatadogAction } from "../../services/datadog";
import { isEqual } from "../helpers/equality";
import { fastDeserialize, fastSerialize } from "../helpers/serialize";

export { fastSerialize, fastDeserialize } from "../helpers/serialize";
export { isEqual } from "../helpers/equality";

export interface CookieOptions {
	expires?: number | Date;
	path?: string;
	domain?: string;
	secure?: boolean;
	sameSite?: "strict" | "lax" | "none";
	retryInterval?: number;
	refreshOnRetrySuccess?: boolean;
	maxRetryDuration?: number;
}

let cookieCache: { value: string; parsed: Record<string, string> } | null = null;

const COOKIE_SIZE_LIMIT = 4096;

// Encode cookie name to ensure it's safe
const encodeCookieName = (name: string): string => {
	return encodeURIComponent(name).replaceAll(/[!'()*]/g, (c) => {
		return "%" + c.codePointAt(0)!.toString(16).toUpperCase();
	});
};

// Decode cookie name
const decodeCookieName = (name: string): string => {
	try {
		return decodeURIComponent(name);
	} catch {
		return name;
	}
};

// Additional encoding for cookie values to handle special characters
const encodeCookieValue = (value: string): string => {
	return encodeURIComponent(value);
};

// Decode cookie value
const decodeCookieValue = (value: string): string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

const parseCookies = (): Record<string, string> => {
	if (typeof document === "undefined") {
		return {};
	}

	const currentCookieString = document.cookie;

	if (cookieCache?.value === currentCookieString) {
		return cookieCache.parsed;
	}

	const cookies: Record<string, string> = {};

	if (!currentCookieString) {
		cookieCache = { value: currentCookieString, parsed: cookies };

		return cookies;
	}

	const cookieArray = currentCookieString.split(";");

	for (const cookie of cookieArray) {
		const [encodedName, encodedValue] = cookie.trim().split("=");

		if (!encodedName || !encodedValue) {
			continue;
		}

		const name = decodeCookieName(encodedName);
		const value = decodeCookieValue(encodedValue);

		cookies[name] = value;
	}

	cookieCache = { value: currentCookieString, parsed: cookies };

	return cookies;
};

const getCookie = (name: string): string | null => {
	const cookies = parseCookies();

	return cookies[name] || null;
};

const createExpiresDate = (expires: number | Date): Date => {
	if (typeof expires === "number") {
		const date = new Date();

		date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);

		return date;
	}

	return expires;
};

const setCookieString = (name: string, value: string, options?: CookieOptions): void => {
	if (typeof document === "undefined") {
		return;
	}

	const encodedName = encodeCookieName(name);
	const encodedValue = encodeCookieValue(value);

	let cookieString = `${encodedName}=${encodedValue}`;

	if (options?.expires) {
		const expiresDate = createExpiresDate(options.expires);

		cookieString += `; expires=${expiresDate.toUTCString()}`;
	}

	const path = options?.path || "/";

	cookieString += `; path=${path}`;

	if (options?.domain) {
		cookieString += `; domain=${options.domain}`;
	}

	// Default to secure unless explicitly set to false
	if (options?.secure !== false) {
		cookieString += "; secure";
	}

	// Default to strict unless explicitly set otherwise
	const sameSite = options?.sameSite || "strict";

	cookieString += `; samesite=${sameSite}`;

	// Throw error if cookie exceeds size limit
	if (cookieString.length > COOKIE_SIZE_LIMIT) {
		throw new Error(
			`Cookie "${name}" size (${cookieString.length} bytes) exceeds 4KB limit. ` +
				"Consider using localStorage or reducing data size."
		);
	}

	document.cookie = cookieString;
	cookieCache = null;
};

const deleteCookie = (name: string, options?: Pick<CookieOptions, "path" | "domain">): void => {
	const deleteOptions: CookieOptions = {
		...options,
		expires: new Date(0),
		// Ensure deletion works with secure cookies
		secure: true,
		sameSite: "strict",
	};

	setCookieString(name, "", deleteOptions);
};

const isCookieAvailable = (): boolean => {
	if (typeof document === "undefined") {
		return false;
	}

	try {
		const testKey = "__test_cookie__";

		setCookieString(testKey, "test", { secure: false }); // Allow test cookie in non-secure contexts
		const testValue = getCookie(testKey);

		deleteCookie(testKey);

		return testValue === "test";
	} catch {
		console.error(
			"Cookies are not available. Please check your browser settings or ensure HTTPS is used."
		);

		return false;
	}
};

export const useCookie = <T>(
	key: string,
	initialValue?: T,
	options?: CookieOptions
): {
	cookieValue: T | null;
	setCookie: (value: T | ((val: T | null) => T)) => void;
	removeCookie: () => void;
} => {
	const [cookieValue, setCookieValue] = useState<T | null>(() => {
		try {
			const cookie = getCookie(key);

			if (cookie === null) {
				return initialValue ?? null;
			}

			return fastDeserialize<T>(cookie);
		} catch (error) {
			console.error("Error reading cookie:", error);

			return initialValue ?? null;
		}
	});

	if (!isCookieAvailable()) {
		console.error(
			"Cookies are not available. Please check your browser settings or ensure HTTPS is used."
		);
	}

	const defaultOptions = useMemo(
		() => ({
			expires: 1, // Consider reducing this default
			secure: true,
			sameSite: "strict" as const,
			...options,
		}),
		[options]
	);

	const setCookie = useCallback(
		(value: T | ((val: T | null) => T)) => {
			if (!isCookieAvailable()) {
				return;
			}

			try {
				const valueToStore = typeof value === "function" ? value(cookieValue) : value;

				if (isEqual(cookieValue, valueToStore)) {
					return;
				}

				const stringValue = fastSerialize(valueToStore);

				setCookieString(key, stringValue, defaultOptions);
				setCookieValue(valueToStore);
			} catch (error) {
				console.error("Error setting cookie:", error);
				// Re-throw if it's a size limit error
				if (error instanceof Error && error.message.includes("exceeds 4KB limit")) {
					throw error;
				}
			}
		},
		[key, cookieValue, defaultOptions]
	);

	const removeCookie = useCallback(() => {
		try {
			deleteCookie(key, { path: options?.path, domain: options?.domain });
			setCookieValue(initialValue ?? null);
		} catch (error) {
			console.error("Error removing cookie:", error);
		}
	}, [key, initialValue, options?.path, options?.domain]);

	const isPageVisible = useRef(true);
	const intervalRef = useRef<NodeJS.Timeout>();
	const retryIntervalRef = useRef<NodeJS.Timeout>();
	const retryStartTime = useRef<number | null>(null);

	const handleSuccessfulRetry = (parsedValue: T) => {
		if (!options?.refreshOnRetrySuccess) return;

		addDatadogAction("cookie_retry_success_refresh", {
			cookieName: key,
			cookieValue:
				typeof parsedValue === "string"
					? parsedValue.substring(0, 10)
					: JSON.stringify(parsedValue).substring(0, 10),
			timestamp: new Date().toISOString(),
		});
		setTimeout(() => {
			globalThis.location.reload();
		}, 100);
	};

	const handleNullCookie = () => {
		const shouldResetToInitial = cookieValue !== null && !isEqual(cookieValue, initialValue);

		if (shouldResetToInitial) {
			setCookieValue(initialValue ?? null);
		}
	};

	const processCookieValue = (currentCookie: string) => {
		const parsedValue = fastDeserialize<T>(currentCookie);

		if (isEqual(cookieValue, parsedValue)) return;

		if (cookieValue === null && parsedValue !== null) {
			handleSuccessfulRetry(parsedValue);
		}
		setCookieValue(parsedValue);
	};

	useEffect(() => {
		const retryInterval = (options?.retryInterval ?? 5) * 1000;
		const maxRetryDuration = (options?.maxRetryDuration ?? 60) * 1000;

		const checkCookieChange = () => {
			if (!isPageVisible.current) return;

			try {
				const currentCookie = getCookie(key);

				if (currentCookie === null) {
					handleNullCookie();

					return;
				}

				processCookieValue(currentCookie);
			} catch (error) {
				console.error("Error checking cookie change:", error);
			}
		};

		const shouldStopRetry = () => {
			const elapsed = Date.now() - (retryStartTime.current || 0);

			return elapsed >= maxRetryDuration;
		};

		const retryPollingCallback = () => {
			if (cookieValue !== null) return;

			if (shouldStopRetry()) {
				stopRetryPolling();

				return;
			}
			checkCookieChange();
		};

		const startRetryPolling = () => {
			if (retryIntervalRef.current) {
				clearInterval(retryIntervalRef.current);
			}

			retryStartTime.current = Date.now();
			retryIntervalRef.current = setInterval(retryPollingCallback, retryInterval);
		};

		const stopRetryPolling = () => {
			if (retryIntervalRef.current) {
				clearInterval(retryIntervalRef.current);
				retryIntervalRef.current = undefined;
			}
			retryStartTime.current = null;
		};

		const startPolling = () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			intervalRef.current = setInterval(checkCookieChange, 1000);

			if (cookieValue === null) {
				startRetryPolling();
			} else {
				stopRetryPolling();
			}
		};

		const stopPolling = () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = undefined;
			}
			stopRetryPolling();
		};

		const handleVisibilityChange = () => {
			if (typeof document === "undefined") {
				return;
			}

			isPageVisible.current = !document.hidden;

			if (document.hidden) {
				stopPolling();
			} else {
				checkCookieChange();
				startPolling();
			}
		};

		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", handleVisibilityChange);
		}

		startPolling();

		return () => {
			stopPolling();
			if (typeof document !== "undefined") {
				document.removeEventListener("visibilitychange", handleVisibilityChange);
			}
		};
	}, [key, cookieValue, initialValue, options?.retryInterval, options?.maxRetryDuration]);

	return { cookieValue, setCookie, removeCookie };
};

export const getAllCookies = (): Record<string, string> => {
	return parseCookies();
};

export const clearAllCookies = (path: string = "/"): void => {
	if (typeof document === "undefined") {
		return;
	}

	const cookies = getAllCookies();

	for (const cookieName of Object.keys(cookies)) {
		deleteCookie(cookieName, { path });
	}
};
